import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TenantContextService } from '../../services/tenant-context.service';
import { CompanyProfileService } from './company-profile.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-company-information',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './company-information.component.html',
})
export class CompanyInformationComponent {
  readonly profile = inject(CompanyProfileService);
  private readonly destroy = inject(DestroyRef);
  private readonly auth = inject(AuthService);
  get canManage(): boolean { return this.auth.hasPermission('Company.Manage'); }
  busy = false;
  editing = false;
  loaded = false;
  error = '';
  readonly form = inject(FormBuilder).nonNullable.group({
    name: [{ value: '', disabled: true }],
    street: ['', Validators.maxLength(255)], city: ['', Validators.maxLength(75)], region: ['', Validators.maxLength(100)], postalCode: ['', Validators.maxLength(12)], country: ['', Validators.maxLength(100)],
  });
  readonly fields = [
    { key: 'street', maxLength: 255, label: 'Street address', autocomplete: 'address-line1' },
    { key: 'city', maxLength: 75, label: 'City', autocomplete: 'address-level2' },
    { key: 'region', maxLength: 100, label: 'State', autocomplete: 'address-level1' },
    { key: 'postalCode', maxLength: 12, label: 'ZIP / Postal code', autocomplete: 'postal-code' },
    { key: 'country', maxLength: 100, label: 'Country', autocomplete: 'country-name' },
  ];
  saved = false;

  constructor() {
    this.load();
    inject(TenantContextService).sessionChanged$.pipe(takeUntilDestroyed()).subscribe(() => this.load());
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => { this.saved = false; });
  }

  save(): void {
    if (!this.editing || !this.loaded || !this.canManage || this.busy || !this.form.dirty) return;
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.busy = true; this.error = '';
    this.profile.save(this.form.getRawValue()).pipe(takeUntilDestroyed(this.destroy)).subscribe({
      next: () => { this.cancel(); this.saved = true; this.busy = false; },
      error: err => { this.busy = false; this.error = err.error?.message ?? 'Company information could not be saved.'; },
    });
  }
  load(): void {
    this.cancel();
    this.busy = true; this.loaded = false; this.error = '';
    this.profile.load().pipe(takeUntilDestroyed(this.destroy)).subscribe({
      next: () => { this.cancel(); this.loaded = true; this.busy = false; },
      error: () => { this.busy = false; this.error = 'Company information could not be loaded.'; },
    });
  }

  edit(): void {
    if (!this.canManage || this.busy || !this.loaded) return;
    this.cancel();
    this.editing = true;
  }

  cancel(): void {
    this.form.reset(this.profile.profile());
    this.saved = false;
    this.editing = false;
    this.error = '';
  }
}
